import lunr, { Query } from 'lunr';

import { SITE_CONFIG } from './site';
import { BlogType } from '../models/blog';
import { GhostTag, GhostPost } from '../models/ghost';


export type GhostTagMap = { [key: string]: GhostTag };

export function getTagsMap() {
  const mappedTags: GhostTagMap = {};
  const tags: GhostTag[] = SITE_CONFIG[process.env.GATSBY_BLOG_TYPE as BlogType].tags;
  tags.forEach(tag => (mappedTags[tag.slug] = tag));
  return tags;
}

export function getCategoryBySlug(
  slug: string
): { name: string; slug: string } | null {
  const categories =
    SITE_CONFIG[process.env.GATSBY_BLOG_TYPE as BlogType].categories;

  const match = categories.find(category => category.slug === slug);
  return match || null;
}

export function searchPosts(
  query: string,
  category: string,
  tags: string[]
): Promise<GhostPost[]> {
  const lunrObj = (window as any).__LUNR__;

  if (!lunrObj) {
    return Promise.resolve([]);
  }

  return lunrObj.__loaded.then((loadedLunr: any) => {
    const lunrIndex = loadedLunr['en'];

    try {
      // tag search
      const searchResults = lunrIndex.index.query((q: Query) => {
        if (query) {
          q.term(query.toLowerCase(), {
            fields: ['title'],
            presence: lunr.Query.presence.REQUIRED,
          });
        }
        if (category) {
          q.term(category.toLowerCase(), {
            fields: ['normalized_category'],
            presence: lunr.Query.presence.REQUIRED,
          });
        }
        if (tags && tags.length > 0) {
          tags.forEach(tag => {
            q.term(tag.toLowerCase(), {
              fields: ['normalized_tags'],
              presence: lunr.Query.presence.REQUIRED,
            });
          });
        }
        q.term('ghostpost', {
          fields: ['type'],
          presence: lunr.Query.presence.REQUIRED,
        });
      });

      return searchResults.map(({ ref }: { ref: any }) => {
        return lunrIndex.store[ref];
      });
    } catch (e) {
      return [];
    }
  });
}

export function getTagBySlug(slug: string): Promise<GhostTag | null> {
  const lunrObj = (window as any).__LUNR__;

  if (!lunrObj) {
    return Promise.resolve(null);
  }

  return lunrObj.__loaded.then((loadedLunr: any) => {
    const lunrIndex = loadedLunr['en'];

    try {
      const results = lunrIndex.index
        .query((q: Query) => {
          q.term(slug, {
            fields: ['slug'],
            presence: lunr.Query.presence.REQUIRED,
          });
          q.term('ghosttag', {
            fields: ['type'],
            presence: lunr.Query.presence.REQUIRED,
          });
        })
        .map(({ ref }: { ref: any }) => {
          return lunrIndex.store[ref];
        });

      if (results.length > 0) {
        const exact = results.find((tag: GhostTag) => tag.slug === slug);
        if (exact) {
          return exact;
        }
        return results[0];
      }

      return null;
    } catch (e) {
      return null;
    }
  });
}

export function countPostsInCategory(category: string): Promise<number> {
  const lunrObj = (window as any).__LUNR__;

  if (!lunrObj) {
    return Promise.resolve(0);
  }

  return lunrObj.__loaded.then((loadedLunr: any) => {
    const lunrIndex = loadedLunr['en'];

    try {
      const results = lunrIndex.index.query((q: Query) => {
        if (category) {
          q.term(category, {
            fields: ['normalized_category'],
            presence: lunr.Query.presence.REQUIRED,
          });
          q.term('ghostpost', {
            fields: ['type'],
            presence: lunr.Query.presence.REQUIRED,
          });
        }
      });

      return results.length;
    } catch (e) {
      return 0;
    }
  });
}

export function getPostsByAuthor(slug: string): Promise<GhostPost[]> {
  const lunrObj = (window as any).__LUNR__;

  if (!lunrObj) {
    return Promise.resolve([]);
  }

  return lunrObj.__loaded.then((loadedLunr: any) => {
    const lunrIndex = loadedLunr['en'];

    try {
      // tag search
      const searchResults = lunrIndex.index.query((q: Query) => {
        q.term(slug.toLowerCase(), {
          fields: ['author'],
          presence: lunr.Query.presence.REQUIRED,
        });
        q.term('ghostpost', {
          fields: ['type'],
          presence: lunr.Query.presence.REQUIRED,
        });
      });

      return searchResults.map(({ ref }: { ref: any }) => {
        return lunrIndex.store[ref];
      });
    } catch (e) {
      return [];
    }
  });
}
